<template>
  <div>
    <Modal
      :title="labels.HOMEPAGE.PROJECT_DIALOG.CARD_TITLE"
      :header-color="headerColor"
      :is-open="dialog"
      :width="'500'"
      :card-actions-enabled="true"
      :disable-close="isAddingProject"
      data-test="addProjectModal"
      @close-dialog="closeDialog"
    >
      <template #body>
        <v-text-field
          v-model="projectName.value"
          class="w-100"
          variant="outlined"
          data-test="inputField"
          data-testid="new-project-name-field"
          :label="labels.HOMEPAGE.PROJECT_DIALOG.PROJECT_NAME"
          required
          :maxlength="CONSTANTS.MAX_LENGTH_INPUT"
          :hint="projectName.infoMessage"
          :persistent-hint="true"
          @keydown.enter="createProject"
          @update:model-value="validateInput(projectName)"
        />
        <v-autocomplete
          v-model="organizationSlug"
          auto-select-first
          :loading="loadingOrganizations"
          :label="localLabels.ORGANIZATION"
          variant="outlined"
          :items="organizations"
          item-title="name"
          item-value="slug"
        />
        <ProjectSlugInput
          v-model="projectSlug"
          :name="projectName.value"
          class="w-100"
          variant="outlined"
          :organization-id-or-slug="organizationSlug"
          @keydown.enter="createProject"
        />
      </template>

      <template #btn-group>
        <v-btn
          :loading="isAddingProject"
          variant="flat"
          color="cyan-dark"
          class="text-white create-project-btn"
          :disabled="projectName.value.trim() === ''"
          data-test="modalBtns"
          data-testid="create-project-btn"
          :prepend-icon="CONSTANTS.ICONS_MDI.ICON_CHECK"
          @click="createProject"
        >
          {{ labels.HOMEPAGE.PROJECT_DIALOG.ADD }}
        </v-btn>
      </template>
    </Modal>
  </div>
</template>
<script lang="ts">
  const localLabels = {
    ORGANIZATION: 'Organization',
  }
</script>
<script lang="ts" setup>
  import CONSTANTS from '@/constants/Config.constants'
  import * as HELPERS from '@/utils/helpers'
  import { Project, IProject } from '@/models/project.model'

  import { validateInput } from '@/utils/helpers'
  import labels from '@/translations/labels.json'
  import { IFormField } from '@/models/classes/FormField'
  import { EventBus } from '@/event-bus'
  import { onMounted, ref } from 'vue'
  import { useRouter } from 'vue-router'
  import Modal from '@/components/Modal/Modal.vue'
  import { injectTyped } from '@/inject'
  import { ServiceKeys } from '@/services/symbols'
  import ProjectSlugInput from './ProjectSlugInput.vue'
  import { storeToRefs } from 'pinia'
  import { useOrganizations } from '@/stores/organizations'
  import { useUrlSearchParams } from '@vueuse/core'

  const { organizations, loadingOrganizations } = storeToRefs(useOrganizations())

  const {
    projectSlug: projectSlugFromUrl,
    organization: organizationSlugFromUrl,
    createProject: createProjectFlag,
  } = useUrlSearchParams('history', { write: false })

  const router = useRouter()
  const isAddingProject = ref(false)
  const dialog = ref(!!createProjectFlag)
  const organizationSlug = ref<string | undefined>(organizationSlugFromUrl as string)
  const projectName = ref<IFormField>({
    name: 'projectName',
    value: '',
    errorMessage: '',
    infoMessage: '',
    isEmpty: true,
    isDirty: false,
  })
  const projectSlug = ref(projectSlugFromUrl as string)
  const headerColor = CONSTANTS.DIALOGS_HEADER_color

  const projectService = injectTyped(ServiceKeys.project)

  onMounted(() => {
    EventBus.on('openDialog', openDialog)
  })

  function openDialog(): void {
    dialog.value = true
  }

  function closeDialog(): void {
    dialog.value = false
    resetFields()
  }

  function resetFields(): void {
    projectName.value.value = ''
    projectName.value.infoMessage = ''
    projectName.value.errorMessage = ''
    projectSlug.value = ''
    isAddingProject.value = false
  }

  function createProject(): void {
    isAddingProject.value = true
    const newProject: IProject = new Project()
    newProject.name = projectName.value.value
    newProject.slug = projectSlug.value
    if (projectName.value.value.trim() !== '') {
      projectService
        .createProjectInOrganization(newProject, organizationSlug.value!)
        .then((data) => {
          EventBus.emit('projectCreatedSucessfull')
          resetFields()
          dialog.value = !dialog.value
          loadProjectList(data)
          if (window.location.href.endsWith('/')) {
            redirectToProjectsPage()
          }
        })
        .catch((e) => {
          isAddingProject.value = false
          HELPERS.showTopAlertMessage(true, 'error', e.data.message)
        })
    }
  }

  function loadProjectList(newProject: Project): void {
    EventBus.emit('addNewProject', newProject)
  }

  function redirectToProjectsPage(): void {
    router.push(CONSTANTS.BASE_ROUTES.PROJECTLIST)
  }
</script>

<style lang="scss" scoped>
  #inputField {
    max-width: 80%;
  }

  .v-dialog {
    overflow-y: hidden;
  }

  #card-actions.v-card__actions {
    position: absolute;
    right: 8.5rem;
    bottom: 0.4em;
  }

  .create-project-btn {
    width: 100%;
  }

  .field-container,
  .btn-container {
    width: 100%;
    display: flex;
    justify-content: center;
  }
</style>
